import "./style.scss";

import React, { FC, useEffect, useRef, useState } from "react";
import { ReactComponent as IconSearch } from "assets/img/icons/icon_Search.svg";
import { ReactComponent as IconChevronDown } from "assets/img/icons/icon_carret-Down.svg";
import { ReactComponent as IconChevronUp } from "assets/img/icons/icon_carret-Up.svg";
import { ReactComponent as IconClose } from "assets/img/icons/icon_Close.svg";

import countryCode from "assets/data/CountryCodes.json";

const SelectCountryEU: FC<{ country: string; field: any; handleChange?: any; disabled?: boolean }> = ({
                                                                                                          field: { name, value, onChange, onBlur },
                                                                                                          handleChange,
                                                                                                          disabled = false
                                                                                                      }) => {
    const selectRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [isOpened, setIsOpened] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredOptions, setFilteredOptions] = useState(countryCode);
    const [isInitialized, setIsInitialized] = useState(false);

    // Trouver la France dans la liste (codeCountry "FRA" for France)
    const france = countryCode.find(c => c.codeCountry === "FRA");

    // Définir la valeur par défaut à la France si aucune valeur n'est sélectionnée
    useEffect(() => {
        if (!isInitialized && !value && france && !disabled) {
            const defaultValue = france.codeCountry;
            onChange({
                target: {
                    name,
                    value: defaultValue,
                },
            });
            if (handleChange) {
                handleChange(defaultValue);
            }
            setIsInitialized(true);
        }
    }, [value, name, onChange, handleChange, france, disabled, isInitialized]);

    // Filter options based on search term
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredOptions(countryCode);
        } else {
            const filtered = countryCode.filter(country => {
                const searchLower = searchTerm.toLowerCase();
                return (
                  country.name.toLowerCase().includes(searchLower) ||
                  country.code.toLowerCase().includes(searchLower) ||
                  country.codeCountry.toLowerCase().includes(searchLower)
                );
            });
            setFilteredOptions(filtered);
        }
    }, [searchTerm]);

    const handleOptionSelect = (optionValue: string) => {
        setIsOpened(false);
        setSearchTerm("");
        onChange({
            target: {
                name,
                value: optionValue,
            },
        });
        if (handleChange) {
            handleChange(optionValue);
        }
        if (onBlur) onBlur({ target: { name } });
    };

    const handleSelectToggle = () => {
        if (!disabled) {
            setIsOpened(!isOpened);
            setSearchTerm("");
            // Focus search input when opened
            if (!isOpened) {
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 100);
            }
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
            setIsOpened(false);
            setSearchTerm("");
        }
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        searchInputRef.current?.focus();
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Find selected country based on current value
    const selectedCountry = countryCode.find((c) => c.codeCountry === value);

    // Get display value for the trigger
    const getTriggerDisplay = () => {
        if (!selectedCountry) {
            return "Pays émetteur";
        }
        return selectedCountry.name;
    };

    return (
      <div
        className={`SelectCountryEU select-wrapper ${disabled ? "disabled" : ""}`}
        ref={selectRef}
      >
          <select
            className="select"
            value={value || ""}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            style={{ display: "none" }}
            disabled={disabled}
          />
          <div className={`custom-select ${isOpened ? "opened" : ""}`}>
              <div
                className={`custom-select__trigger ${disabled ? "disabled" : ""}`}
                onClick={handleSelectToggle}
              >
                    <span className="d-flex align-items-center">
                        {selectedCountry && (
                          <img
                            src={`${process.env.PUBLIC_URL}/img/flags/${selectedCountry.code.toLowerCase()}.png`}
                            alt={selectedCountry.name}
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        <span className="trigger-text">{getTriggerDisplay()}</span>
                    </span>
                  <span className="trigger-icon">
                        {isOpened ? <IconChevronUp /> : <IconChevronDown />}
                    </span>
              </div>

              {isOpened && (
                <div className="custom-options">
                    {/* Search Input */}
                    <div className="search-container">
                        <div className="search-input-wrapper">
                            <IconSearch className="search-icon" />
                            <input
                              ref={searchInputRef}
                              type="text"
                              className="search-input"
                              placeholder="Rechercher un pays..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                              <IconClose
                                className="clear-icon"
                                onClick={handleClearSearch}
                              />
                            )}
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="options-list">
                        {filteredOptions.length > 0 ? (
                          filteredOptions.map(({ code, codeCountry, name }, index) => (
                            <span
                              className={`custom-option ${
                                codeCountry === value ? "selected" : ""
                              }`}
                              data-value={codeCountry}
                              key={index}
                              onClick={() => handleOptionSelect(codeCountry)}
                            >
                                        <img
                                          src={`${process.env.PUBLIC_URL}/img/flags/${code.toLowerCase()}.png`}
                                          alt={name}
                                          onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = 'none';
                                          }}
                                        />
                                        <span className="option-text">
                                            {name}
                                        </span>
                                {codeCountry === value && (
                                  <span className="check-mark">✓</span>
                                )}
                                    </span>
                          ))
                        ) : (
                          <div className="no-results">
                              Aucun résultat trouvé
                          </div>
                        )}
                    </div>
                </div>
              )}
          </div>
      </div>
    );
};

export default SelectCountryEU;